import { create } from 'zustand';

const DB_STATE_KEY = 'sgrc.backup.databaseState';

/**
 * @typedef {{
 *   kind: 'backup'|'restore'|'dump',
 *   label: string,
 *   detail?: string|null,
 *   startedAt: number,
 *   phase: 'starting'|'running'|'finishing'|'verifying',
 *   targetName?: string|null,
 *   stepKey?: string|null,
 * }} ActiveBackupJob
 *
 * @typedef {{
 *   snapshotName: string,
 *   restoredAt: number,
 *   pitrTime?: string|null,
 * }} DatabaseRestoreState
 */

function loadDatabaseState() {
    try {
        const raw = localStorage.getItem(DB_STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.snapshotName || !parsed?.restoredAt) return null;
        return parsed;
    } catch {
        return null;
    }
}

function persistDatabaseState(state) {
    try {
        if (!state) localStorage.removeItem(DB_STATE_KEY);
        else localStorage.setItem(DB_STATE_KEY, JSON.stringify(state));
    } catch {
        /* ignore quota / private mode */
    }
}

export const useBackupStore = create((set) => ({
    local: [],
    pbm: [],
    pbmRaw: null,
    schedule: null,
    status: null,
    loading: false,
    statusLoading: false,
    error: null,
    /** @type {ActiveBackupJob|null} */
    activeJob: null,
    lastJobResult: null,
    /** @type {DatabaseRestoreState|null} */
    databaseState: loadDatabaseState(),

    setLoading: (loading) => set({ loading }),
    setStatusLoading: (statusLoading) => set({ statusLoading }),
    setError: (error) => set({ error }),
    clearError: () => set({ error: null }),
    setList: ({ local = [], pbm = [], pbmRaw = null, schedule = null } = {}) =>
        set({ local, pbm, pbmRaw, schedule: schedule ?? null }),
    setStatus: (status) => set({ status }),
    setSchedule: (schedule) => set({ schedule }),
    setActiveJob: (activeJob) => set({ activeJob }),
    patchActiveJob: (patch) => set((s) => (
        s.activeJob ? { activeJob: { ...s.activeJob, ...patch } } : {}
    )),
    clearActiveJob: () => set({ activeJob: null }),
    setLastJobResult: (lastJobResult) => set({ lastJobResult }),
    clearLastJobResult: () => set({ lastJobResult: null }),
    setDatabaseState: (databaseState) => {
        persistDatabaseState(databaseState);
        set({ databaseState });
    },
    clearDatabaseState: () => {
        persistDatabaseState(null);
        set({ databaseState: null });
    },
}));
